import apiClient from "./client";
import { getBookByCode, getBookTests } from "./books";
import type { ResolvedTest, TestSummary } from "@/types";

/**
 * Tests API.
 * Backend endpoint: GET /tests/{id}
 *
 * The frontend routes use a book code (level, e.g. "C18") plus a per-book test
 * number (1-4). The backend identifies a test by its numeric DB id, so
 * `resolveTest` translates (level, number) -> the full Test object (with id).
 */

export async function getTest(testId: number): Promise<TestSummary> {
  const { data } = await apiClient.get<TestSummary>(`/tests/${testId}`);
  return data;
}

const _resolveCache = new Map<string, ResolvedTest>();

export async function resolveTest(
  level: string,
  number: string | number
): Promise<ResolvedTest> {
  const key = `${String(level).toUpperCase()}#${number}`;
  const cached = _resolveCache.get(key);
  if (cached) return cached;

  const book = await getBookByCode(level);
  if (!book) throw new Error(`Book "${level}" was not found.`);

  const tests = await getBookTests(book.id);
  const test = tests.find((t) => Number(t.number) === Number(number));
  if (!test) {
    throw new Error(`Test ${number} was not found in book ${level}.`);
  }

  const resolved: ResolvedTest = { ...test, book };
  _resolveCache.set(key, resolved);
  return resolved;
}
